import { useEffect } from "react";
import type { JSX } from "react";
import { Icon } from "../../components/ui";
import { useTriggerDetail, useResolvedWatches } from "./useTriggers";
import { EntityCardRenderer } from "../../components/shared/EntityCardRenderer";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import { formatTimeAgo } from "../../utils/time";

const OUTCOME_LABELS: Record<string, string> = {
  spawned: "Fired",
  skipped_gate: "Skipped (not relevant)",
  skipped_cooldown: "Skipped (cooldown)",
  error: "Error",
};

const OUTCOME_COLORS: Record<string, string> = {
  spawned: "text-green-400",
  skipped_gate: "text-yellow-400",
  skipped_cooldown: "text-content-muted",
  error: "text-red-400",
};

const EVENT_KIND_LABELS: Record<string, string> = {
  sync_ingested: "New message/email",
  entity_created: "Entity created",
  facet_attached: "Data updated",
};

export function TriggerDetailPanel({
  triggerId,
  onBack,
  onDelete,
  onToggleStatus,
}: {
  readonly triggerId: string;
  readonly onBack: () => void;
  readonly onDelete: () => Promise<void>;
  readonly onToggleStatus: (status: string) => Promise<void>;
}): JSX.Element {
  const runtime = useAppRuntime();
  const { trigger, loading, history, loadHistory } = useTriggerDetail(triggerId);

  const resolvedWatches = useResolvedWatches(trigger?.watched_entities, runtime);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  if (loading || !trigger) {
    return (
      <div className="px-4 py-3">
        <button type="button" onClick={onBack} className="flex items-center gap-1 text-[12px] text-agent-text-muted hover:text-agent-text cursor-pointer bg-transparent border-none mb-3">
          <Icon name="arrow-left" size={14} />
          Back
        </button>
        <div className="py-4 text-center text-[12px] text-agent-text-muted">Loading...</div>
      </div>
    );
  }

  const isActive = trigger.status === "active";

  return (
    <div className="px-4 py-3 space-y-3">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-[12px] text-agent-text-muted hover:text-agent-text cursor-pointer bg-transparent border-none">
        <Icon name="arrow-left" size={14} />
        Back
      </button>

      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <Icon name="zap" size={14} className="text-accent shrink-0" />
          <h3 className="text-[14px] font-semibold text-agent-text">{trigger.name}</h3>
        </div>
        <span className={`text-[11px] ml-5 ${isActive ? "text-green-400" : "text-content-muted"}`}>
          {trigger.status}
        </span>
      </div>

      {/* Watches */}
      {resolvedWatches.length > 0 && (
        <div>
          <span className="text-[11px] font-bold tracking-wider text-agent-text-muted uppercase block mb-1">Watches</span>
          <div className="space-y-1">
            {resolvedWatches.map((entity) =>
              entity.error ? (
                <span key={entity.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-edge bg-surface-tertiary text-[12px] text-content-muted">
                  <Icon name="circle-alert" size={12} className="text-yellow-400" />
                  {(entity.data.name as string | undefined) ?? entity.id}
                </span>
              ) : (
                <EntityCardRenderer
                  key={entity.id}
                  schemaId={entity.schema_id}
                  data={entity.data}
                  runtime={runtime}
                />
              ),
            )}
          </div>
        </div>
      )}

      {/* Event types */}
      <div>
        <span className="text-[11px] font-bold tracking-wider text-agent-text-muted uppercase block mb-1">When</span>
        <div className="flex flex-wrap gap-1.5">
          {trigger.event_kinds.map((kind) => (
            <span key={kind} className="text-[11px] px-2 py-0.5 rounded-full bg-agent-surface border border-agent-border text-agent-text">
              {EVENT_KIND_LABELS[kind] ?? kind}
            </span>
          ))}
        </div>
      </div>

      {/* Gate prompt */}
      {trigger.gate_prompt && (
        <div>
          <span className="text-[11px] font-bold tracking-wider text-agent-text-muted uppercase block mb-1">Gate</span>
          <p className="text-[12px] text-agent-text bg-agent-surface rounded-md px-3 py-2 border border-agent-border whitespace-pre-wrap">
            {trigger.gate_prompt}
          </p>
        </div>
      )}

      {/* Action prompt */}
      <div>
        <span className="text-[11px] font-bold tracking-wider text-agent-text-muted uppercase block mb-1">Action</span>
        <p className="text-[12px] text-agent-text bg-agent-surface rounded-md px-3 py-2 border border-agent-border whitespace-pre-wrap">
          {trigger.action_prompt}
        </p>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-3 text-[12px] text-agent-text-muted">
        <span>Fired {trigger.firing_count}x</span>
        {trigger.cooldown_seconds > 0 && <span>Cooldown {trigger.cooldown_seconds}s</span>}
        {trigger.max_firings != null && <span>Max {trigger.max_firings}</span>}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void onToggleStatus(isActive ? "paused" : "active")}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium cursor-pointer bg-agent-surface border border-agent-border text-agent-text hover:bg-surface-hover"
        >
          <Icon name={isActive ? "pause" : "activity"} size={12} />
          {isActive ? "Pause" : "Resume"}
        </button>
        <button
          type="button"
          onClick={() => void onDelete()}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium cursor-pointer bg-transparent border border-red-500/30 text-red-400 hover:bg-red-500/10"
        >
          <Icon name="trash" size={12} />
          Delete
        </button>
      </div>

      {/* Fire History */}
      {history.length > 0 && (
        <div>
          <span className="text-[11px] font-bold tracking-wider text-agent-text-muted uppercase block mb-1.5">
            Recent activity
          </span>
          <div className="space-y-1">
            {history.map((exec, i) => (
              <div key={i} className="flex items-center justify-between px-2 py-1.5 text-[11px]">
                <span className={OUTCOME_COLORS[exec.outcome] ?? "text-content-muted"}>
                  {OUTCOME_LABELS[exec.outcome] ?? exec.outcome}
                </span>
                <span className="text-agent-text-muted">{formatTimeAgo(exec.fired_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
